// What each stock and sales number in Atlas means, written once: the
// Glossary page lists every term, and the (i) popups next to a number show
// that term's entry. Must follow the Rails math (Inventory::StockHealth,
// Purchase::OrderQuantity); change both together.

export interface GlossaryTerm {
  // Anchor on the Glossary page: /admin/glossary#soh-doc
  id: string
  term: string
  // The words behind a short term ("Stock on hand" for SOH).
  fullName?: string
  summary: string
  points: string[]
  example?: string
  note?: string
}

export interface GlossarySection {
  title: string
  terms: GlossaryTerm[]
}

export const GLOSSARY: GlossarySection[] = [
  {
    title: "Stock",
    terms: [
      {
        id: "soh",
        term: "SOH",
        fullName: "Stock on hand",
        summary: "The physical stock count of the product, inside the warehouses of this market.",
        points: [
          "Counts every warehouse allocated to one of the market's sales channels (the Allocated tab on the Warehouses page).",
          "Stock still on its way is not counted. See Incoming.",
          "Negative stock is counted as it is. A negative number means the stock feed has bad data, so it is shown, not hidden.",
        ],
      },
      {
        id: "incoming",
        term: "Incoming",
        summary: "Units on purchase orders that are still being made or shipped to this market.",
        points: [
          "Counts order lines that are Producing or Shipping.",
          "An order line belongs to a market by its import country and warehouse note.",
          "Counts every line, with or without an arrival date.",
          "A line past its arrival date counts as arriving today.",
        ],
        note: "Every order line should have an arrival date. A line without one is counted here, but it cannot be placed on a day, so Projected out of stock and Order by leave it out.",
      },
    ],
  },
  {
    title: "Sales pace",
    terms: [
      {
        id: "average-daily-move",
        term: "Average daily move",
        summary: "How many units leave the market on an average day: sold or given as gifts, over the last 90 days.",
        points: [
          "Every sale in the market counts, from whichever warehouse it shipped.",
          "Gifts count, because a gift empties the shelf too.",
          "Only days the product could sell count. Days before it first came into stock in the market, or first sold if that was earlier, are left out.",
          "Days out of stock are left out: a day is left out when the market had no stock and nothing went out that day.",
          "A day when stock ran out but something still sold stays in.",
          "A day missing from the stock history stays in: the data feed missed it, the product was still there.",
          "The 90 days end yesterday. Today is not counted yet.",
          "Blank, or “Not enough sales data”, when nothing went out in the last 90 days.",
        ],
        example: "450 units out in 90 days, with 15 days out of stock: 450 ÷ 75 days = 6 a day. A product first in stock 20 days ago that sold 120 units: 120 ÷ 20 days = 6 a day.",
        note: "On the Push review, each warehouse row counts only the sales shipped from that warehouse, but the Total row counts every sale in the market. Some sales have no warehouse recorded, and some ship from warehouses not allocated to the market, so the warehouse rows can add up to less than the Total.",
      },
    ],
  },
  {
    title: "Days of cover",
    terms: [
      {
        id: "soh-doc",
        term: "SOH DOC",
        fullName: "Stock on hand days of cover",
        summary: "How many days today's stock on hand lasts at the average daily move.",
        points: [
          "SOH ÷ average daily move, rounded down.",
          "Only stock already in the warehouses. Incoming orders and the plan are not counted.",
          "Blank when nothing went out in the last 90 days.",
        ],
        example: "200 on hand, moving 10 a day: 20 days.",
      },
      {
        id: "total-doc",
        term: "Total DOC",
        fullName: "Total days of cover",
        summary: "How many days the stock on hand plus everything incoming lasts at the average daily move.",
        points: [
          "(SOH + incoming) ÷ average daily move, rounded down.",
          "Counts every incoming order line, with or without an arrival date.",
          "Blank when nothing went out in the last 90 days.",
        ],
        example: "70 on hand, moving 10 a day, 300 arriving in 30 days: (70 + 300) ÷ 10 = 37 days.",
        note: "Total DOC treats incoming stock as if it were already on the shelf. In the example, stock really runs out on day 7 and the shelf is empty until day 30. Projected out of stock shows that day.",
      },
      {
        id: "projected-out-of-stock",
        term: "Projected out of stock",
        summary: "How many days until stock runs out, counting incoming orders on the day they land.",
        points: [
          "Stock runs down at the average daily move. With nothing incoming, this equals SOH DOC.",
          "Each incoming order adds its units on its arrival date. A late order counts as arriving today. An order with no arrival date is left out.",
          "The plan is not used, because it often runs higher than real sales. Only when nothing went out in the last 90 days, a product in this market's published plan runs down by the planned units each day instead.",
          "“> N” means nothing went out in the last 90 days and stock lasts past the plan's N days.",
          "Blank when there is no plan and nothing went out in the last 90 days.",
        ],
        note: "Why it can differ from SOH DOC: this number counts incoming orders on the day they land. Status is based on this number.",
      },
    ],
  },
  {
    title: "Buying",
    terms: [
      {
        id: "lead-time",
        term: "Lead time",
        summary: "How many days a purchase order takes from the day it is placed until the goods land.",
        points: [
          "The longest lead time among the product's suppliers.",
          "75 days when no supplier lead time is on record, marked “(Default lead time)”.",
        ],
      },
      {
        id: "buffer",
        term: "Buffer",
        summary: "How many days of stock are left after an order placed today would land.",
        points: [
          "Projected out of stock (days) − lead time.",
          "Below 0: stock runs out before an order placed today can land.",
        ],
        example: "Runs out in 90 days, lead time 75 days: buffer 15 days.",
      },
      {
        id: "order-by",
        term: "Order by",
        summary: "The last day to place a purchase order so the goods land before stock runs out.",
        points: [
          "The out-of-stock day minus the lead time.",
          "When an order already on its way ends a stockout before a new order could land, ordering now cannot help that stockout. Order by skips it and works from the next out-of-stock day.",
          "“Overdue” when that day has already passed: stock runs out before a new order can land.",
        ],
        example: "Runs out on Nov 4, lead time 75 days: order by Aug 21.",
      },
      {
        id: "recommended-po",
        term: "Recommended PO",
        summary: "How many units to order today.",
        points: [
          "The order must cover one lead time after it lands, because the next order placed on that day takes one more lead time to arrive.",
          "Units = expected sales from the day it lands until one lead time later − stock still left on landing day − incoming orders landing in that time.",
          "Expected sales: the average daily move. Only when nothing went out in the last 90 days, the plan's units instead.",
          "Every market that needs buying is added up, then raised to the supplier's minimum order quantity (MOQ) when below it.",
        ],
      },
    ],
  },
  {
    title: "Status",
    terms: [
      {
        id: "status",
        term: "Status",
        summary: "How urgent the product is to buy, from its buffer.",
        points: [
          "Nothing incoming: the buffer is taken on the day stock on hand runs out.",
          "Critical: buffer below 0 days. Stock runs out before an order placed today can land.",
          "Order soon: buffer of 0 to 7 days.",
          "Healthy: buffer of 8 to 22 days.",
          "Overstock: buffer of more than 22 days.",
          "Something incoming (with or without an arrival date): the buffer is taken on the last day stock runs out, once every dated order has landed. Critical and Order soon on the same days as above.",
          "Restocking: something incoming, and a buffer of more than 7 days. The orders on the way are enough, so there is nothing to buy now.",
          "Not enough sales data: no plan, and nothing went out in the last 90 days.",
          "No Status when nothing went out in the last 90 days and stock lasts past the plan (“> N”).",
        ],
        example: "Lead time 75 days, nothing incoming, stock runs out in 90 days: buffer 15 days, Healthy.",
        note: "Need buying = Critical or Order soon, for products on the For sale tab.",
      },
      {
        id: "not-moving",
        term: "Not moving",
        summary: "Nothing sold or given as gifts in the market in the last 90 days.",
        points: ["Not moving products have no average daily move, so no DOC. They get a Status only from the plan, when they are in it."],
      },
      {
        id: "new",
        term: "New",
        summary: "A Not moving product that first came into stock in the market within the last 90 days: too new to have sold.",
        points: ["A product with no stock history is not New."],
      },
      {
        id: "gift",
        term: "Gift product",
        summary: "A product where 90% or more of the units out in the last 90 days left as gifts.",
        points: ["Gift products follow gift campaigns, not daily sales, so they never count as Need buying."],
      },
    ],
  },
  {
    title: "Push",
    terms: [
      {
        id: "push-review-list",
        term: "Push review list",
        summary: "The products to discuss in the Push review: stock that won't sell within the cut-off at the current pace, and is worth enough to talk about.",
        points: [
          "A product joins the list when its days of cover reach the cut-off (120 days to start), or when nothing was sold or gifted in the last 90 days (No movement).",
          "Its excess value must also reach the minimum: 10,000,000 ₫ in Vietnam, $500 in other markets, to start.",
          "A product with no unit cost (COGS) has no excess value, so it is never on the list.",
          "Incoming orders never put a product on the list. When a listed product has dated orders coming, its days of cover once they land show next to it.",
          "Stock counts only the market's allocated warehouses (the Allocated tab on the Warehouses page). Units out are sold plus gifted units shipped from them.",
          "Each tab is a product group. A product with no group is under Other. Each tab lists the biggest excess value first.",
        ],
        example: "5 units, nothing sold in 90 days, unit cost 300,000 ₫: excess value 1,500,000 ₫, below 10,000,000 ₫, so it is left off.",
        note: "Each person sets their own cut-off and minimum at the top of the page. They are kept in that person's browser, so changing them never changes anyone else's list. The Home page counts the list at the starting numbers.",
      },
      {
        id: "excess",
        term: "Excess",
        summary: "Stock on hand that won't sell within the Push cut-off (120 days to start) at the current pace.",
        points: [
          "Excess units = stock on hand − units out per day × cut-off days, never below 0.",
          "Incoming orders are not counted.",
          "A product with no movement: all of its stock on hand is excess.",
          "Excess value = excess units × unit cost (COGS), in the market's currency.",
        ],
      },
    ],
  },
]

const BY_ID = new Map(GLOSSARY.flatMap((section) => section.terms).map((term) => [term.id, term]))

export function glossaryTerm(id: string): GlossaryTerm {
  const term = BY_ID.get(id)
  if (!term) throw new Error(`No glossary term "${id}"`)
  return term
}
